import { translator } from "../../translation/translator";
import { SpinnerRender } from "../../util/spinner/spinner-render";
import { spinner } from "../../util/spinner/spinner";
import { phpAuthUser, phpUrl } from "../../core/php-data-access";
import { SearchCategoryPickerSetup } from "../../components/picker/search-category/search-category-picker-setup";
import { SearchBrandPickerSetup } from "../../components/picker/search-brand/search-brand-picker-setup";
import { SearchUserPickerSetup } from "../../components/picker/search-user/search-user-picker-setup";
import { SearchProductUpdatedByPickerSetup } from "../../components/picker/search-user/search-product-updated-by-picker-setup";
import { ChoicesControl } from "../../components/choices-control/choices-control-setup";
import { ChoicesControlUtils, type ChoicesInstance } from "../../components/choices-control/choices-control-utils";
import { ModalSearchSelectionIO } from "../../components/modal-search/modal-search-selection-io";
import { Roles } from "../../constants/roles";
import { UISymbols } from "../../constants/ui-symbols";
import { renderProductRatingStatsHtml } from "../../components/product-rating/product-rating-summary-render";
import { CommonUtils } from "../../util/common-utils";
import { isDefaultNoImagePath } from "../../util/product-image";
import {
    resolveCategorySymbol,
    buildCategoryPlaceholderHtml,
    buildCategoryIconHtml,
} from "../../components/category-icon/resolve-category-symbol";
import { buildCategoryBreadcrumbHtml } from "../../components/category-icon/category-breadcrumb";
import { buildBrandIconHtml } from "../../components/menu-icon/brand-icon";
import { SearchMenuForm } from "./search-menu-form";
import { SearchMenuListener } from "./search-menu-listener";
import type { SearchMenuData, SearchMenuPrimitive, SearchMenuRecord } from "./search-menu-api";
import type { SearchMenuPersistedAdvanced, SearchMenuPersistedState } from "./strage/search-menu-strage-utils";

type SearchMenuSelections = {
    categories: string[];
    brands: string[];
    users: string[];
    products: string[];
    keywords: string[];
};

type SearchMenuActionPayload = {
    selections: SearchMenuSelections;
    query: string;
};

type SearchMenuPickerController = {
    setSelectedIds?: (ids: string[]) => void;
    render?: () => void;
    mergeLabelMap?: (labelMap: Map<string, string>) => void;
};

type SearchMenuRenderOptions = {
    mode?: string;
    onApply?: ((payload: SearchMenuActionPayload) => void) | null;
    onClear?: ((payload: SearchMenuActionPayload) => void) | null;
    onSetProduct?: ((product: SearchMenuRecord) => void) | null;
    getQuery?: (() => string) | null;
    persistedState?: SearchMenuPersistedState | null;
};

export class SearchMenuRender {
    private static readonly postsSearchBasePath = "/posts";
    public menuContainer: HTMLElement | null;
    public onApply: ((payload: SearchMenuActionPayload) => void) | null;
    public onClear: ((payload: SearchMenuActionPayload) => void) | null;
    public onSetProduct: ((product: SearchMenuRecord) => void) | null;
    public getQuery: (() => string) | null;
    private _resultItemsById: Map<string, SearchMenuRecord>;
    private _advancedHydrated: boolean;
    private _advancedFlagsSyncInitialized: boolean;
    public selectedCategoryIds: Set<string>;
    public selectedBrandIds: Set<string>;
    public selectedUserIds: Set<string>;
    public selectedUpdatedByIds: Set<string>;
    private _categoryPicker: SearchMenuPickerController | null;
    private _brandPicker: SearchMenuPickerController | null;
    private _userPicker: SearchMenuPickerController | null;
    private _productUpdatedByPicker: SearchMenuPickerController | null;
    private readonly form: SearchMenuForm;

    /**
     * Render clear button label using HTML output policy.
     * @param {Element|null} button
     */
    public static renderClearButtonLabel(button: Element | null): void {
        if (!(button instanceof HTMLElement)) return;
        button.innerHTML = UISymbols.MODAL_CLOSE;
    }

    /**
     * Create modal overlay shell and append it to body.
     * DOM creation is centralized in render layer.
     * @returns {{ overlay: HTMLDivElement, container: HTMLDivElement } | null}
     */
    public static renderModalOverlayShell(): { overlay: HTMLDivElement; container: HTMLDivElement } | null {
        const host = document.createElement("div");
        host.innerHTML =
            `<div class="search-menu-modal-overlay" data-role="${Roles.SEARCH_MENU_OVERLAY}" style="display:none;">` +
            `<div class="search-menu-dropdown search-menu-dropdown--modal" data-role="${Roles.SEARCH_MENU_CONTAINER}" style="display:none;"></div>` +
            "</div>";

        const overlay = host.firstElementChild;
        const container = overlay?.firstElementChild;
        if (!(overlay instanceof HTMLDivElement) || !(container instanceof HTMLDivElement)) {
            return null;
        }

        document.body.appendChild(overlay);
        return { overlay, container };
    }

    /**
     * Create modal overlay wrapper for existing menu container and append to body.
     * @param {HTMLElement} existing
     * @returns {HTMLDivElement | null}
     */
    public static renderModalOverlayWithExisting(existing: HTMLElement): HTMLDivElement | null {
        if (!(existing instanceof HTMLElement)) return null;

        const host = document.createElement("div");
        host.innerHTML = `<div class="search-menu-modal-overlay" data-role="${Roles.SEARCH_MENU_OVERLAY}" style="display:none;"></div>`;

        const overlay = host.firstElementChild;
        if (!(overlay instanceof HTMLDivElement)) {
            return null;
        }

        existing.classList.add("search-menu-dropdown", "search-menu-dropdown--modal");
        document.body.appendChild(overlay);
        overlay.appendChild(existing);
        return overlay;
    }

    /** @returns {Set<string>} */
    public createStringSet(): Set<string> {
        return new Set<string>();
    }

    /** @returns {ReturnType<typeof SearchCategoryPickerSetup.setupForSearchMenu>|null} */
    public createCategoryPickerSlot(): SearchMenuPickerController | null {
        return null;
    }

    /** @returns {ReturnType<typeof SearchBrandPickerSetup.setupForSearchMenu>|null} */
    public createBrandPickerSlot(): SearchMenuPickerController | null {
        return null;
    }

    /** @returns {ReturnType<typeof SearchUserPickerSetup.setupForSearchMenu>|null} */
    public createUserPickerSlot(): SearchMenuPickerController | null {
        return null;
    }

    /** @returns {ReturnType<typeof SearchProductUpdatedByPickerSetup.setupForSearchMenu>|null} */
    public createProductUpdatedByPickerSlot(): SearchMenuPickerController | null {
        return null;
    }

    public constructor() {
        this.menuContainer = null;
        this.onApply = null;
        this.onClear = null;
        this.onSetProduct = null;
        this.getQuery = null;
        this.form = new SearchMenuForm();
        this._resultItemsById = new Map();

        this._advancedHydrated = false;
        this._advancedFlagsSyncInitialized = false;

        this.selectedCategoryIds = this.createStringSet();

        this.selectedBrandIds = this.createStringSet();

        this.selectedUserIds = this.createStringSet();

        this.selectedUpdatedByIds = this.createStringSet();

        this._categoryPicker = this.createCategoryPickerSlot();

        this._brandPicker = this.createBrandPickerSlot();

        this._userPicker = this.createUserPickerSlot();

        this._productUpdatedByPicker = this.createProductUpdatedByPickerSlot();
    }

    /**
     * Set initial selection ids for category/brand/user pickers.
     * Intended to be called before first render/initialization.
     *
     * @param {{ categories?: string[], brands?: string[], users?: string[] }} selections
     */
    public setInitialPickerSelections(selections?: {
        categories?: string[];
        brands?: string[];
        users?: string[];
    }): void {
        const selectionValues = selections ?? {};
        const categories = Array.isArray(selectionValues.categories) ? selectionValues.categories : [];
        const brands = Array.isArray(selectionValues.brands) ? selectionValues.brands : [];
        const users = Array.isArray(selectionValues.users) ? selectionValues.users : [];

        this.selectedCategoryIds = new Set(categories.map((v) => String(v ?? "").trim()).filter(Boolean));
        this.selectedBrandIds = new Set(brands.map((v) => String(v ?? "").trim()).filter(Boolean));
        this.selectedUserIds = new Set(users.map((v) => String(v ?? "").trim()).filter(Boolean));
    }

    /**
     * Get translated text for a key
     * @param {string} key - Translation key
     * @returns {string}
     */
    public t(key: string): string {
        return translator.getTranslation(key) || key;
    }

    /**
     * Render loading state into menu container
     * @param {HTMLElement} container
     */
    public renderLoading(container: HTMLElement): void {
        const spinner = SpinnerRender.createInlineSpinner({ size: "normal" });

        const results = this.form.getSearchMenuResults(container);
        const html = `
            <div class="search-menu-loading">
                ${spinner.outerHTML}
                <div class="loading-text"></div>
            </div>
        `;

        if (results instanceof HTMLElement) {
            results.innerHTML = html;
        } else {
            container.innerHTML = html;
        }
        translator.updateUITranslations();
    }

    /**
     * Render error state into menu container
     * @param {HTMLElement} container
     */
    public renderError(container: HTMLElement): void {
        const results = this.form.getSearchMenuResults(container);
        const html = `
            <div class="search-menu-error">
                <div class="error-text" data-translate="load_failed">การโหลดล้มเหลว</div>
            </div>
        `;

        if (results instanceof HTMLElement) {
            results.innerHTML = html;
        } else {
            container.innerHTML = html;
        }
        translator.updateUITranslations();
    }

    /**
     * Render the complete search menu
     * @param {HTMLElement} container - Container element for the menu
     * @param {Object} data - Menu data from API
     * @param {Function} onClose - Callback when menu should be closed
     */
    public render(
        container: HTMLElement,
        data: SearchMenuData,
        onClose: (() => void) | null,
        options?: SearchMenuRenderOptions,
    ): void {
        this.menuContainer = container;

        const renderOptions = options ?? {};

        const mode = String(renderOptions.mode || "default");

        this.onApply = typeof renderOptions.onApply === "function" ? renderOptions.onApply : null;
        this.onClear = typeof renderOptions.onClear === "function" ? renderOptions.onClear : null;
        this.onSetProduct = typeof renderOptions.onSetProduct === "function" ? renderOptions.onSetProduct : null;
        this.getQuery = typeof renderOptions.getQuery === "function" ? renderOptions.getQuery : null;

        const hasPreparedDom = !!this.form.getSearchMenuRoot(container);

        // Blade-prepared DOM is required for all modes.
        // (post-product-picker is also rendered via Blade into @stack('modals').)
        if (!hasPreparedDom) {
            console.error(
                "[SearchMenuRender] Blade-prepared search-menu DOM not found (mode=%s). Cannot initialize search menu.",
                mode,
            );
            return;
        }

        // Apply translations to dynamically generated elements
        translator.updateUITranslations();
        // Hydrate from URL before initializing Choices controls so multi-selects can read hidden values.
        this.hydrateAdvancedFromUrl({ persistedState: renderOptions.persistedState });

        // Enhance any <select data-choices-control> inside the menu (e.g. status multi-select)
        ChoicesControl.init(container);

        // Initialize category picker UI (chips + open modal)
        this.initializeCategoryPicker(data);

        // Initialize brand picker UI (chips + open modal)
        this.initializeBrandPicker(data);

        // Initialize user picker UI (chips + open modal)
        this.initializeUserPicker();

        // Initialize admin-only updated_by picker UI (chips + open modal)
        this.initializeProductUpdatedByPicker();

        // Wire Apply/Clear button handlers
        this.attachSearchButtonListener();

        // Attach close button listener
        this.attachCloseListener(onClose);

        // Advanced search panel (show more/less + URL hydration)
        this.initializeAdvancedSearchUi();
        this.initializeTargetFieldVisibility();

        // Keyword search clear button inside the search menu.
        this.initializeKeywordClearButton();

        // Advanced flags multi-select: keep legacy checkbox fields in sync.
        this.initializeAdvancedFlagsSync();

        // Mark as initialized (Blade-prepared DOM is hidden by default; JS only toggles visibility)
        container.dataset.searchMenuInitialized = "1";
    }

    public isAdminEditor(): boolean {
        try {
            const role = phpAuthUser()?.role || null;
            return role === "admin" || role === "editor";
        } catch (_e) {
            return false;
        }
    }

    public toDatetimeLocalValue(dateString: string | number | Date | null | undefined): string {
        if (!dateString) return "";
        const d = new Date(String(dateString));
        if (Number.isNaN(d.getTime())) return "";

        const pad = (n) => String(n).padStart(2, "0");
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const mi = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    }

    public toUtcIsoFromDatetimeLocal(value: string | number | Date | null | undefined): string {
        const raw = String(value || "").trim();
        if (!raw) return "";
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return "";
        return d.toISOString();
    }

    private initializeKeywordClearButton(): void {
        if (!this.menuContainer) return;

        const input = this.form.getNamedInput(this.menuContainer, "content_search");
        const clearButton = this.form.getSearchMenuKeywordClearButton(this.menuContainer);
        if (!(input instanceof HTMLInputElement) || !(clearButton instanceof HTMLButtonElement)) {
            return;
        }

        if (clearButton.dataset.searchMenuKeywordClearBound !== "1") {
            clearButton.dataset.searchMenuKeywordClearBound = "1";
            SearchMenuRender.renderClearButtonLabel(clearButton);
            SearchMenuListener.bindClick(clearButton, (event) => {
                event.preventDefault();
                input.value = "";
                this.syncKeywordClearButton();
                input.focus();
            });
        }

        if (input.dataset.searchMenuKeywordClearBound !== "1") {
            input.dataset.searchMenuKeywordClearBound = "1";
            SearchMenuListener.bindInput(input, () => {
                this.syncKeywordClearButton();
            });
        }

        this.syncKeywordClearButton();
    }

    private syncKeywordClearButton(): void {
        if (!this.menuContainer) return;

        const input = this.form.getNamedInput(this.menuContainer, "content_search");
        const clearButton = this.form.getSearchMenuKeywordClearButton(this.menuContainer);
        if (!(input instanceof HTMLInputElement) || !(clearButton instanceof HTMLButtonElement)) {
            return;
        }

        if (input.value.trim() !== "") {
            clearButton.classList.add("show");
            return;
        }

        clearButton.classList.remove("show");
    }

    public hydrateAdvancedFromUrl(options?: { persistedState?: SearchMenuPersistedState | null }): void {
        if (this._advancedHydrated) return;
        this._advancedHydrated = true;

        try {
            const params = new URLSearchParams(window.location.search);
            const hydrateOptions = options ?? {};

            const persisted = hydrateOptions.persistedState ?? null;
            const persistedSelections = persisted?.selections ?? null;
            const persistedAdvanced = persisted?.advanced ?? null;

            // Determine active tab from page URL path
            const isPostsPage =
                typeof globalThis !== "undefined" &&
                typeof globalThis.location?.pathname === "string" &&
                globalThis.location.pathname.startsWith("/posts");
            const activeTabKey = isPostsPage ? "posts" : "products";
            const activeTabPanel = this.menuContainer
                ? this.form.getAdvancedTabPanel(this.menuContainer, activeTabKey)
                : null;
            const panelScope: ParentNode = activeTabPanel ?? this.menuContainer ?? document;

            const setCheckbox = (
                name: string,
                checked: boolean,
                scope: ParentNode = this.menuContainer ?? document,
            ): void => {
                const el = this.form.getNamedCheckbox(scope, name);
                if (el instanceof HTMLInputElement) el.checked = !!checked;
            };

            const setText = (name: string, value: SearchMenuPrimitive): void => {
                const el = this.menuContainer ? this.form.getNamedInput(this.menuContainer, name) : null;
                if (el instanceof HTMLInputElement) el.value = String(value || "");
            };

            const urlHasNewOnly = params.has("new_only");
            const urlHasRecommendOnly = params.has("recommend_only");
            const urlHasRankingOnly = params.has("ranking_only") || params.get("sort") === "likes_count";
            const urlHasReviewedOnly = params.has("reviewed_only");
            const urlHasExcludeTextOnly = params.has("exclude_text_only");
            const urlHasAnyFlags =
                urlHasNewOnly ||
                urlHasRecommendOnly ||
                urlHasRankingOnly ||
                urlHasReviewedOnly ||
                urlHasExcludeTextOnly;

            const persistedFlags = persistedAdvanced
                ? ChoicesControl.normalizeStringList(persistedAdvanced["advanced_flags"])
                : [];

            const isNewOnly = urlHasAnyFlags ? params.get("new_only") === "1" : persistedFlags.includes("new_only");

            const isRecommendOnly = urlHasAnyFlags
                ? params.get("recommend_only") === "1"
                : persistedFlags.includes("recommend_only");

            const isRankingOnly = urlHasAnyFlags
                ? params.get("ranking_only") === "1" || params.get("sort") === "likes_count"
                : persistedFlags.includes("ranking_only");

            const isReviewedOnly = urlHasAnyFlags
                ? params.get("reviewed_only") === "1"
                : persistedFlags.includes("reviewed_only");

            const isExcludeTextOnly = urlHasAnyFlags
                ? params.get("exclude_text_only") === "1"
                : persistedFlags.includes("exclude_text_only");

            // Backward-compatible checkbox hydration (fields may be hidden in UI)
            setCheckbox("new_only", isNewOnly);
            setCheckbox("recommend_only", isRecommendOnly);
            setCheckbox("ranking_only", isRankingOnly);
            // reviewed_only and exclude_text_only exist in both tab panels; set in active panel scope
            setCheckbox("reviewed_only", isReviewedOnly, panelScope);
            setCheckbox("exclude_text_only", isExcludeTextOnly, panelScope);

            // New UI: Choices multi-select (hidden input name="advanced_flags")
            const flags = [];
            if (isNewOnly) flags.push("new_only");
            if (isRecommendOnly) flags.push("recommend_only");
            if (isRankingOnly) flags.push("ranking_only");
            if (isReviewedOnly) flags.push("reviewed_only");
            setText("advanced_flags", JSON.stringify(flags));

            const urlContentSearch = params.get("content_search");
            const persistedContentSearch = persistedAdvanced ? String(persistedAdvanced["content_search"] ?? "") : "";
            const contentSearchValue =
                urlContentSearch !== null ? String(urlContentSearch || "") : String(persistedContentSearch || "");
            setText("content_search", contentSearchValue);

            // Rating (multi-select)
            // - preferred: rating (csv or repeated)
            // - fallback: persisted state
            const urlRating =
                params.getAll("rating").join(",") ||
                params.get("rating") ||
                params.getAll("ratings").join(",") ||
                params.get("ratings") ||
                "";

            const urlRatingList = ChoicesControl.normalizeStringList(urlRating);

            const persistedRating = persistedAdvanced
                ? ChoicesControl.normalizeStringList(persistedAdvanced["rating"])
                : [];

            const ratingList = urlRatingList.length > 0 ? urlRatingList : persistedRating;
            setText("rating", JSON.stringify(ratingList));

            // content_search_fields: multi-select component writes to hidden input[name="content_search_fields"]
            // as a JSON array string; accept CSV/JSON from the URL.
            // NOTE: content_search_fields only makes sense when a keyword (content_search) exists.
            // If content_search_fields is present in the URL without content_search, ignore it and keep Blade defaults.
            const rawFields =
                params.getAll("content_search_fields").join(",") || params.get("content_search_fields") || "";
            const fields = ChoicesControl.normalizeStringList(rawFields);

            // content_search_fields: read from active tab panel (different options per tab)
            const fieldsHidden = this.form.getNamedHiddenInput(panelScope, "content_search_fields");

            const clearFields = () => {
                if (!(fieldsHidden instanceof HTMLInputElement)) return;
                fieldsHidden.value = JSON.stringify([]);
            };

            const hasFieldsParam =
                params.getAll("content_search_fields").length > 0 || params.has("content_search_fields");

            const persistedFields = persistedAdvanced
                ? ChoicesControl.normalizeStringList(persistedAdvanced["content_search_fields"])
                : [];

            const setFieldsHidden = (value: string) => {
                if (fieldsHidden instanceof HTMLInputElement) fieldsHidden.value = value;
            };

            if (hasFieldsParam) {
                if (String(contentSearchValue || "").trim() !== "" && fields.length > 0) {
                    setFieldsHidden(JSON.stringify(fields));
                } else {
                    clearFields();
                }
            } else if (String(contentSearchValue || "").trim() !== "" && persistedFields.length > 0) {
                setFieldsHidden(JSON.stringify(persistedFields));
            } else {
                clearFields();
            }

            if (this.isAdminEditor()) {
                const urlUpdatedBy = params.getAll("updated_by").join(",") || params.get("updated_by") || "";
                const persistedUpdatedBy = persistedSelections
                    ? ChoicesControl.normalizeStringList(persistedSelections["updated_by"]).join(",")
                    : "";
                const rawUpdatedBy = String(urlUpdatedBy || "").trim() ? urlUpdatedBy : persistedUpdatedBy;
                setText("updated_by", rawUpdatedBy);

                // Keep picker state in sync with the URL/hydrated hidden field.
                this.selectedUpdatedByIds = new Set(
                    ModalSearchSelectionIO.parseIdsFromValue(rawUpdatedBy, { mode: "multi" })
                        .map((v) => String(v ?? "").trim())
                        .filter(Boolean),
                );

                // product_status: multi-select component writes to hidden input[name="product_status"].
                // Keep reading legacy status keys so older persisted state and URLs still hydrate.
                const urlStatus = params.get("product_status") || params.get("status") || "";
                const persistedStatus = persistedAdvanced
                    ? (persistedAdvanced["product_status"] ?? persistedAdvanced["status"])
                    : "";
                const statusList =
                    String(urlStatus || "").trim() !== ""
                        ? ChoicesControl.normalizeStringList(urlStatus)
                        : ChoicesControl.normalizeStringList(persistedStatus);
                if (statusList.length > 0) {
                    setText("product_status", JSON.stringify(statusList));
                }

                const fromUtc = params.get("updated_at_from") || "";
                const toUtc = params.get("updated_at_to") || "";

                const persistedFrom = persistedAdvanced ? String(persistedAdvanced["updated_at_from"] ?? "") : "";
                const persistedTo = persistedAdvanced ? String(persistedAdvanced["updated_at_to"] ?? "") : "";

                // persisted values are stored in datetime-local format
                const fromValue = String(fromUtc || "").trim() ? this.toDatetimeLocalValue(fromUtc) : persistedFrom;
                const toValue = String(toUtc || "").trim() ? this.toDatetimeLocalValue(toUtc) : persistedTo;

                setText("updated_at_from", fromValue);
                setText("updated_at_to", toValue);
            }
        } catch (e) {
            console.warn("[SearchMenuRender] Failed to hydrate advanced filters from URL", e);
        }
    }

    public initializeAdvancedFlagsSync(): void {
        if (this._advancedFlagsSyncInitialized) return;
        this._advancedFlagsSyncInitialized = true;
        if (!this.menuContainer) return;

        const select = this.form.getChoicesSelect(this.menuContainer, "advanced_flags");
        if (!(select instanceof HTMLSelectElement)) return;

        const setCheckbox = (name: string, checked: boolean): void => {
            const el = this.menuContainer ? this.form.getNamedCheckbox(this.menuContainer, name) : null;
            if (el instanceof HTMLInputElement) el.checked = !!checked;
        };

        const sync = () => {
            const values = ChoicesControl.getSelectedValues(select);
            setCheckbox("new_only", values.includes("new_only"));
            setCheckbox("recommend_only", values.includes("recommend_only"));
            setCheckbox("ranking_only", values.includes("ranking_only"));
            setCheckbox("reviewed_only", values.includes("reviewed_only"));
        };

        // Initial sync + keep in sync with user edits.
        sync();
        SearchMenuListener.bindChange(select, sync);
    }

    public isAdvancedPanelActiveByCurrentState(): boolean {
        if (!this.menuContainer) return false;

        const hasValues = (name: string): boolean => {
            const el = this.form.getNamedInput(this.menuContainer, name);
            if (!(el instanceof HTMLInputElement)) return false;
            return String(el.value || "").trim().length > 0;
        };

        const hasListValues = (name: string): boolean => {
            const hidden = this.form.getNamedHiddenInput(this.menuContainer, name);
            if (!(hidden instanceof HTMLInputElement)) return false;
            return ChoicesControl.normalizeStringList(hidden.value).length > 0;
        };

        const hasChecked = (name: string, root: ParentNode = this.menuContainer): boolean => {
            const el = this.form.getNamedCheckbox(root, name);
            return el instanceof HTMLInputElement ? el.checked : false;
        };

        const flags = hasListValues("advanced_flags");
        const contentSearch = hasValues("content_search");
        const contentFields = hasListValues("content_search_fields");
        const rating = hasListValues("rating");
        const hasContentFilters = contentSearch || (contentSearch && contentFields);
        const activePanel = this.form.getActiveAdvancedTabPanel(this.menuContainer) ?? this.menuContainer;
        const reviewedOnly = hasChecked("reviewed_only", activePanel);
        const excludeTextOnly = hasChecked("exclude_text_only", activePanel);

        const hasAdmin =
            this.isAdminEditor() &&
            (hasValues("updated_by") ||
                hasListValues("product_status") ||
                hasValues("updated_at_from") ||
                hasValues("updated_at_to"));

        return !!(flags || hasContentFilters || rating || reviewedOnly || excludeTextOnly || hasAdmin);
    }

    public setAdvancedPanelOpen(open) {
        if (!this.menuContainer) return;

        const isOpen = !!open;
        const toggleRoot = this.form.getSearchAdvancedToggle(this.menuContainer);

        // New show-more component path
        if (toggleRoot instanceof HTMLElement && toggleRoot.tagName.toLowerCase() === "show-more") {
            toggleRoot.toggleAttribute("open", isOpen);
            return;
        }

        // Legacy fallback path
        if (!(toggleRoot instanceof HTMLButtonElement)) return;
        toggleRoot.setAttribute("aria-expanded", isOpen ? "true" : "false");

        translator.updateUITranslations();
    }

    public syncAdvancedPanelExpandedState() {
        this.setAdvancedPanelOpen(this.isAdvancedPanelActiveByCurrentState());
    }

    public switchAdvancedTab(tabKey: string): void {
        if (!this.menuContainer) return;

        const tabs = this.form.getAllAdvancedTabs(this.menuContainer);
        const panels = this.form.getAllAdvancedTabPanels(this.menuContainer);

        tabs.forEach((tab) => {
            const isActive = tab.getAttribute("data-tab") === tabKey;
            tab.classList.toggle("is-active", isActive);
            tab.setAttribute("aria-selected", isActive ? "true" : "false");
            tab.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
            const isActive = panel.getAttribute("data-panel") === tabKey;
            panel.classList.toggle("is-active", isActive);
            if (isActive) {
                panel.removeAttribute("hidden");
                panel.style.display = "";
            } else {
                panel.setAttribute("hidden", "");
                panel.style.display = "none";
            }
        });
    }

    private scrollToAdvancedTabsRoot(): void {
        if (!this.menuContainer) return;
        const scrollRoot = this.form.getSearchMenuScrollBody(this.menuContainer);
        const tabsRow = this.menuContainer.querySelector(`[data-role="${Roles.SEARCH_ADVANCED_TABS}"]`);
        if (!(scrollRoot instanceof HTMLElement) || !(tabsRow instanceof HTMLElement)) return;

        const reducedMotion =
            typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

        const rootRect = scrollRoot.getBoundingClientRect();
        const tabsTop = tabsRow.getBoundingClientRect().top - rootRect.top + scrollRoot.scrollTop;

        scrollRoot.scrollTo({
            top: Math.max(0, tabsTop - 8),
            behavior: reducedMotion ? "auto" : "smooth",
        });
    }

    public syncTargetFieldVisibility(): void {
        if (!this.menuContainer) return;

        const targetInput = this.form.getSearchMenuTargetInput(this.menuContainer);
        const isPostsTarget = targetInput instanceof HTMLInputElement ? targetInput.checked : false;
        this.switchAdvancedTab(isPostsTarget ? "posts" : "products");
    }

    public initializeTargetFieldVisibility(): void {
        if (!this.menuContainer) return;

        // Determine initial tab from current page path (posts page → posts tab)
        const isPostsPage =
            typeof globalThis !== "undefined" &&
            typeof globalThis.location?.pathname === "string" &&
            globalThis.location.pathname.startsWith("/posts");

        const targetInput = this.form.getSearchMenuTargetInput(this.menuContainer);
        if (targetInput instanceof HTMLInputElement && isPostsPage) {
            targetInput.checked = true;
        }

        this.syncTargetFieldVisibility();

        if (!(targetInput instanceof HTMLInputElement)) return;
        if (targetInput.dataset.searchMenuTargetFieldBound === "1") return;
        targetInput.dataset.searchMenuTargetFieldBound = "1";

        // Target toggle → switch tab
        SearchMenuListener.bindChange(targetInput, () => {
            this.syncTargetFieldVisibility();
        });

        // Tab click → switch tab + sync Target toggle (bidirectional)
        const tabs = this.form.getAllAdvancedTabs(this.menuContainer);
        tabs.forEach((tab) => {
            if (tab.dataset.searchMenuTabBound === "1") return;
            tab.dataset.searchMenuTabBound = "1";
            SearchMenuListener.bindClick(tab, (e) => {
                e.preventDefault();
                const tabKey = tab.getAttribute("data-tab") || "products";
                this.switchAdvancedTab(tabKey);
                this.scrollToAdvancedTabsRoot();
                if (targetInput instanceof HTMLInputElement) {
                    targetInput.checked = tabKey === "posts";
                    targetInput.dispatchEvent(new Event("change", { bubbles: true }));
                }
            });
        });
    }

    public initializeAdvancedSearchUi() {
        if (!this.menuContainer) return;

        const toggleRoot = this.form.getSearchAdvancedToggle(this.menuContainer);
        const panel = this.form.getSearchAdvancedPanel(this.menuContainer);
        if (!(panel instanceof HTMLElement)) return;

        // Hydration is performed in render() before Choices initialization.

        const prefersReducedMotion = () => {
            try {
                const mm = globalThis?.matchMedia;
                return typeof mm === "function" ? !!mm("(prefers-reduced-motion: reduce)")?.matches : false;
            } catch (_e) {
                return false;
            }
        };

        const scrollPanelIntoView = () => {
            // Scroll within the menu body (not the whole window).
            const scrollRoot =
                this.form.getSearchMenuScrollBody(this.menuContainer) ||
                this.form.getSearchMenuRoot(this.menuContainer);
            if (!(scrollRoot instanceof HTMLElement)) {
                // Fallback: best-effort.
                panel.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "nearest" });
                return;
            }

            // Only scroll downward.
            // Goal: reveal the newly opened panel, but keep at least part of the toggle visible.
            const padding = 12;

            const rootRect = scrollRoot.getBoundingClientRect();
            const panelTop = panel.getBoundingClientRect().top - rootRect.top + scrollRoot.scrollTop;
            const toggleSource = toggleRoot instanceof HTMLElement ? toggleRoot : null;
            if (!(toggleSource instanceof HTMLElement)) return;

            const toggleRect = toggleSource.getBoundingClientRect();
            const toggleTop = toggleRect.top - rootRect.top + scrollRoot.scrollTop;
            const toggleBottom = toggleTop + toggleRect.height;

            // Use scrollHeight so this works even while max-height is animating.
            const panelBottom = panelTop + Math.max(0, panel.scrollHeight) + padding;
            const visibleBottom = scrollRoot.scrollTop + scrollRoot.clientHeight;

            // Cap: keep the bottom edge of the toggle still visible (so "Show less" doesn't vanish).
            const keepVisiblePx = 10;
            const maxScrollTopToKeepToggle = Math.max(0, toggleBottom - keepVisiblePx);

            const requiredScrollTop = panelBottom > visibleBottom ? panelBottom - scrollRoot.clientHeight : null;

            let target = null;
            if (requiredScrollTop !== null) {
                target = Math.max(scrollRoot.scrollTop, Math.min(requiredScrollTop, maxScrollTopToKeepToggle));
            } else {
                // Panel already fits, but user still expects a small scroll-down on "Show more".
                const smallDelta = 60;
                const candidate = scrollRoot.scrollTop + smallDelta;
                if (candidate > scrollRoot.scrollTop) {
                    target = Math.min(candidate, maxScrollTopToKeepToggle);
                }
            }

            if (target === null || target === scrollRoot.scrollTop) return;

            scrollRoot.scrollTo({
                top: target,
                behavior: prefersReducedMotion() ? "auto" : "smooth",
            });
        };

        this.syncAdvancedPanelExpandedState();

        if (toggleRoot instanceof HTMLElement && toggleRoot.tagName.toLowerCase() === "show-more") {
            SearchMenuListener.bindShowMoreToggle(toggleRoot, (event) => {
                const detail = event instanceof CustomEvent ? event.detail : null;
                const expanded = !!(detail && typeof detail === "object" && "expanded" in detail && detail.expanded);
                if (!expanded) return;

                requestAnimationFrame(() => {
                    scrollPanelIntoView();
                    setTimeout(() => scrollPanelIntoView(), 90);
                });
            });
            return;
        }

        if (!(toggleRoot instanceof HTMLButtonElement)) return;

        SearchMenuListener.bindClick(toggleRoot, () => {
            const expanded = toggleRoot.getAttribute("aria-expanded") === "true";
            this.setAdvancedPanelOpen(!expanded);

            if (!expanded) {
                requestAnimationFrame(() => {
                    scrollPanelIntoView();
                    setTimeout(() => scrollPanelIntoView(), 90);
                });
            }
        });
    }

    /**
     * Initialize category picker (chips + independent modal)
     * @param {SearchMenuData} data
     */
    public initializeCategoryPicker(data: SearchMenuData): void {
        if (!this.menuContainer) return;

        this._categoryPicker = SearchCategoryPickerSetup.setupForSearchMenu({
            menuRoot: this.menuContainer,
            apiData: data,
            initialSelectedIds: Array.from(this.selectedCategoryIds),
            onSelectionChange: (ids) => {
                this.selectedCategoryIds = new Set((ids || []).map((v) => String(v)));
            },
        });
    }

    /**
     * Initialize brand picker (chips + independent modal)
     * @param {SearchMenuData} data
     */
    public initializeBrandPicker(data: SearchMenuData): void {
        if (!this.menuContainer) return;

        this._brandPicker = SearchBrandPickerSetup.setupForSearchMenu({
            menuRoot: this.menuContainer,
            apiData: data,
            initialSelectedIds: Array.from(this.selectedBrandIds),
            onSelectionChange: (ids) => {
                this.selectedBrandIds = new Set((ids || []).map((v) => String(v)));
            },
        });
    }

    /**
     * Initialize user picker (chips + independent modal)
     */
    public initializeUserPicker() {
        if (!this.menuContainer) return;

        this._userPicker = SearchUserPickerSetup.setupForSearchMenu({
            menuRoot: this.menuContainer,
            initialSelectedIds: Array.from(this.selectedUserIds),
            onSelectionChange: (ids) => {
                this.selectedUserIds = new Set((ids || []).map((v) => String(v)));
            },
        });
    }

    /**
     * Initialize Product Updated By picker (admin-only)
     * - Keeps hidden input[name="updated_by"] in sync for backward-compatible serialization.
     */
    public initializeProductUpdatedByPicker() {
        if (!this.menuContainer) return;
        if (!this.isAdminEditor()) return;

        const hiddenUpdatedBy = this.form.getNamedHiddenInput(this.menuContainer, "updated_by");
        const initial = (() => {
            // Prefer in-memory state (survives open/close without relying on DOM).
            const stateIds = Array.from(this.selectedUpdatedByIds);
            if (stateIds.length > 0) return stateIds;

            if (hiddenUpdatedBy instanceof HTMLInputElement) {
                const ids = ModalSearchSelectionIO.parseIdsFromValue(hiddenUpdatedBy.value, { mode: "multi" });
                if (ids.length > 0) return ids;
            }

            return [];
        })();

        // Ensure hidden input reflects the initial state.
        if (hiddenUpdatedBy instanceof HTMLInputElement) {
            hiddenUpdatedBy.value = ModalSearchSelectionIO.serializeIds(initial, { mode: "multi" });
        }
        this.selectedUpdatedByIds = new Set(initial.map((v) => String(v ?? "").trim()).filter(Boolean));

        this._productUpdatedByPicker = SearchProductUpdatedByPickerSetup.setupForSearchMenu({
            menuRoot: this.menuContainer,
            initialSelectedIds: initial,
            onSelectionChange: (ids) => {
                this.selectedUpdatedByIds = new Set((ids || []).map((v) => String(v ?? "").trim()).filter(Boolean));
                if (hiddenUpdatedBy instanceof HTMLInputElement) {
                    hiddenUpdatedBy.value = ModalSearchSelectionIO.serializeIds(ids, { mode: "multi" });
                }
            },
        });
    }

    /**
     * Ensure user-based pickers reflect current URL query params.
     * Also resolves user id -> display name via API so chips show the correct labels.
     *
     * Intended to run on every menu open (even after initialization).
     */
    public syncUserPickersFromUrl() {
        if (!this.menuContainer) return;

        const userIds = [];
        const updatedByIds = [];

        try {
            const params = new URLSearchParams(window.location.search);

            const rawUser =
                params.getAll("user_id").join(",") ||
                params.get("user_id") ||
                params.getAll("user").join(",") ||
                params.get("user") ||
                "";

            userIds.push(
                ...ModalSearchSelectionIO.parseIdsFromValue(rawUser, { mode: "multi" })
                    .map((v) => String(v ?? "").trim())
                    .filter(Boolean),
            );

            if (this.isAdminEditor()) {
                const rawUpdatedBy = params.getAll("updated_by").join(",") || params.get("updated_by") || "";
                updatedByIds.push(
                    ...ModalSearchSelectionIO.parseIdsFromValue(rawUpdatedBy, { mode: "multi" })
                        .map((v) => String(v ?? "").trim())
                        .filter(Boolean),
                );
            }
        } catch (e) {
            console.warn("[SearchMenuRender] Failed to parse URL for user pickers", e);
        }

        // Apply selection state
        this.selectedUserIds = new Set(userIds);
        if (this._userPicker) {
            this._userPicker.setSelectedIds?.(userIds);
            this._userPicker.render?.();
        }

        if (this.isAdminEditor()) {
            this.selectedUpdatedByIds = new Set(updatedByIds);

            const hiddenUpdatedBy = this.form.getNamedHiddenInput(this.menuContainer, "updated_by");
            if (hiddenUpdatedBy instanceof HTMLInputElement) {
                hiddenUpdatedBy.value = ModalSearchSelectionIO.serializeIds(updatedByIds, { mode: "multi" });
            }

            if (this._productUpdatedByPicker) {
                this._productUpdatedByPicker.setSelectedIds?.(updatedByIds);
                this._productUpdatedByPicker.render?.();
            }
        }

        // Note: label resolution (id -> name) is handled outside of Render (see SearchMenuApi).
    }

    /**
     * Apply a user label map (id -> display name) to any user-based pickers.
     * This method is UI-only; API fetching is handled by a separate *-api.js module.
     *
     * @param {Map<string,string>} labelMap
     */
    public applyUserLabels(labelMap) {
        if (!labelMap || typeof labelMap?.get !== "function") return;

        try {
            if (this._userPicker) {
                this._userPicker.mergeLabelMap?.(labelMap);
                this._userPicker.render?.();
            }

            if (this._productUpdatedByPicker) {
                this._productUpdatedByPicker.mergeLabelMap?.(labelMap);
                this._productUpdatedByPicker.render?.();
            }
        } catch (e) {
            console.warn("[SearchMenuRender] Failed to apply user labels", e);
        }
    }

    /**
     * Collect user ids currently represented by user-based pickers.
     * @returns {string[]}
     */
    public getUserIdsToResolve() {
        const ids = new Set();
        for (const id of this.selectedUserIds || []) ids.add(String(id));
        for (const id of this.selectedUpdatedByIds || []) ids.add(String(id));
        return Array.from(ids)
            .map((v) => String(v ?? "").trim())
            .filter(Boolean);
    }

    /**
     * Menu DOM persists between opens; re-sync picker UI from hidden inputs.
     * This prevents stale/incorrect chip state (e.g. updated_by drifting to user_id).
     */
    public syncPickersFromHidden() {
        try {
            if (!this.menuContainer) return;

            // Review User Search picker: source of truth is internal state.
            // (No hidden field in this menu; serialized from selectedUserIds.)

            // Product Update User Search picker: source of truth is hidden input[name="updated_by"].
            if (this.isAdminEditor() && this._productUpdatedByPicker) {
                const ids = Array.from(this.selectedUpdatedByIds);

                // Force hidden to match in-memory state.
                const hiddenUpdatedBy = this.form.getNamedHiddenInput(this.menuContainer, "updated_by");
                if (hiddenUpdatedBy instanceof HTMLInputElement) {
                    hiddenUpdatedBy.value = ModalSearchSelectionIO.serializeIds(ids, { mode: "multi" });
                }

                this._productUpdatedByPicker.setSelectedIds?.(ids);
                this._productUpdatedByPicker.render?.();
            }
        } catch (e) {
            console.warn("[SearchMenuRender] Failed to sync pickers from hidden inputs", e);
        }
    }

    /**
     * Attach search button listener
     */
    public attachSearchButtonListener() {
        if (!this.menuContainer) return;

        const applyBtn = this.form.getSearchMenuConfirmButton(this.menuContainer);
        const clearBtn = this.form.getSearchMenuClearActionButton(this.menuContainer);

        if (applyBtn) {
            SearchMenuListener.bindClick(applyBtn, () => {
                const isProductPicker = this.menuContainer?.dataset.searchMenuMode === "post-product-picker";

                if (applyBtn instanceof HTMLButtonElement && !isProductPicker) {
                    spinner.showButtonLoader(applyBtn, { translateKey: "spinner_proccesing" });
                }

                // Prevent global page overlay spinner for this button-driven navigation.
                document.documentElement.setAttribute("data-suppress-page-loader-once", "1");

                if (this.onApply) {
                    this.onApply({
                        selections: this.getAllSelections(),
                        query: this.getQuery ? String(this.getQuery() || "") : "",
                    });
                    return;
                }

                this.executeSearch();
            });
        }

        if (clearBtn) {
            SearchMenuListener.bindClick(clearBtn, () => {
                if (this.onClear) {
                    this.onClear({
                        selections: this.getAllSelections(),
                        query: this.getQuery ? String(this.getQuery() || "") : "",
                    });
                } else {
                    this.clearAllSelections();
                    this.renderResultsEmpty();
                }
            });
        }

        this.attachResultsListener();
    }

    /**
     * Enable/disable the Apply(Search) button (used to prevent rapid repeat).
     * @param {boolean} disabled
     */
    public setApplyDisabled(disabled) {
        const applyBtn = this.menuContainer ? this.form.getSearchMenuConfirmButton(this.menuContainer) : null;
        if (applyBtn instanceof HTMLButtonElement) {
            applyBtn.disabled = !!disabled;
        }
    }

    /**
     * Blade renders the results panel with hidden/display:none by default (it's only
     * meaningful once a search has run, e.g. post-product-picker mode). Reveal it
     * whenever we're about to show loading/empty/error/result content.
     */
    private revealResultsContainer(root: HTMLElement): void {
        root.hidden = false;
        root.style.display = "";
    }

    /**
     * Render results - loading state
     */
    public renderResultsLoading() {
        const root = this.menuContainer ? this.form.getSearchMenuResults(this.menuContainer) : null;
        if (!(root instanceof HTMLElement)) return;
        this.revealResultsContainer(root);

        const spinner = SpinnerRender.createInlineSpinner({ size: "small" });
        root.innerHTML = `
            <div class="search-menu-results__state search-menu-results__state--loading">
                ${spinner.outerHTML}
                <span data-translate="loading">${this.t("loading")}</span>
            </div>
        `;
        translator.updateUITranslations();
    }

    /**
     * Render results - empty state
     */
    public renderResultsEmpty() {
        const root = this.menuContainer ? this.form.getSearchMenuResults(this.menuContainer) : null;
        if (!(root instanceof HTMLElement)) return;
        this.revealResultsContainer(root);
        root.innerHTML = `
            <div class="search-menu-results__state search-menu-results__state--empty" data-translate="search_no_results">
                ไม่พบข้อมูล
            </div>
        `;
        translator.updateUITranslations();
    }

    /**
     * Render results - error state
     * @param {string} message
     */
    public renderResultsError(message = "") {
        const root = this.menuContainer ? this.form.getSearchMenuResults(this.menuContainer) : null;
        if (!(root instanceof HTMLElement)) return;
        this.revealResultsContainer(root);
        const safeMsg = message ? String(message) : "";
        root.innerHTML = `
            <div class="search-menu-results__state search-menu-results__state--error">
                <span data-translate="load_failed">การโหลดล้มเหลว</span>
                ${safeMsg ? `<div class="search-menu-results__error-message">${safeMsg}</div>` : ""}
            </div>
        `;
        translator.updateUITranslations();
    }

    /**
     * post-product-picker fallback (ISS-02-209): the brand was resolved but no
     * real product matched the current filters — offer the brand's own
     * product_status=brand placeholder product as a selectable result, marked
     * with a "NOT FOUND" watermark so it reads as "link to the brand itself",
     * not a real product. Returns "" if the product has no id. Also
     * registers it into _resultItemsById so the shared click handler
     * (attachResultsListener() -> onSetProduct()) picks it up exactly like
     * any other result row, with no special-casing.
     */
    private buildBrandPlaceholderRowHtml(product: SearchMenuRecord): string {
        const id = String(product["product_id"] ?? "");
        if (!id) return "";

        this._resultItemsById.set(id, product);

        const brandName = String(product["brand_name"] ?? product["product_name"] ?? "");
        const brandId = String(product["brand_id"] ?? "").trim();
        const rawImage = String(product["image_url"] ?? product["image"] ?? "");
        const imageUrl = (() => {
            const value = rawImage ? rawImage.trim().replace(/\\/g, "/") : "";
            if (!value) return "";
            if (value.toUpperCase() === "NO_IMAGE") return "";
            if (isDefaultNoImagePath(value)) return "";
            if (value.startsWith("http")) return value;
            if (value.startsWith("/") || value.includes("/")) {
                return CommonUtils.resolveUrl(value.startsWith("/") ? value : `/${value}`);
            }
            const built = brandId ? phpUrl.productImage(brandId, value) : phpUrl.productImage(value);
            if (!built || isDefaultNoImagePath(built)) return "";
            return built.startsWith("http") ? built : CommonUtils.resolveUrl(built);
        })();

        return `
            <div class="search-menu-results__brand-fallback-caption">
                <span data-translate="search_brand_fallback_before">${this.t("search_brand_fallback_before")}</span><strong>${brandName}</strong><span data-translate="search_brand_fallback_after">${this.t("search_brand_fallback_after")}</span>
            </div>
            <div class="search-menu-result search-menu-result--not-found" data-product-id="${id}">
                <div class="search-menu-result__body">
                    <div class="search-menu-result__thumb">
                        ${buildCategoryPlaceholderHtml(resolveCategorySymbol(null, (s) => this.form.hasCategoryIconSymbol(s)))}
                        ${imageUrl ? `<img src="${imageUrl}" alt="" loading="lazy" referrerpolicy="no-referrer" onerror="this.onerror=null;this.remove();" />` : ""}
                        <span class="search-menu-result__not-found-badge" data-translate="search_brand_fallback_badge">${this.t("search_brand_fallback_badge")}</span>
                    </div>
                    <div class="search-menu-result__meta">
                        <div class="search-menu-result__name">${brandName}</div>
                        <div class="search-menu-result__brand">${buildBrandIconHtml(24)}${brandName}</div>
                    </div>
                </div>
                <div class="search-menu-result__actions">
                    <button type="button" class="btn-search-set" data-role="${Roles.SEARCH_MENU_SET_PRODUCT}" data-product-id="${id}">
                        <span data-translate="set">ตั้งค่า</span>
                    </button>
                </div>
            </div>
        `;
    }

    /**
     * Render product results list. When exactly one brand is selected
     * (ISS-02-209), brandPlaceholder is always appended as the last row —
     * not just when items is empty — so "the exact product isn't in this
     * list" stays a one-click option alongside real matches, not only when
     * there happen to be zero of them.
     */
    public renderProductResults(items: SearchMenuRecord[], brandPlaceholder: SearchMenuRecord | null = null): void {
        const root = this.menuContainer ? this.form.getSearchMenuResults(this.menuContainer) : null;
        if (!(root instanceof HTMLElement)) return;
        this.revealResultsContainer(root);

        const list = Array.isArray(items) ? items : [];
        this._resultItemsById = new Map();

        if (list.length === 0 && !brandPlaceholder) {
            this.renderResultsEmpty();
            return;
        }

        const rows = list
            .map((item) => {
                const id = String(item["product_id"] ?? item["id"] ?? "");
                if (!id) return "";

                this._resultItemsById.set(id, item);

                const brand = isRecord(item["brand"]) ? item["brand"] : null;

                const name = String(item["product_name"] ?? item["name"] ?? item["title"] ?? "");
                const brandName = String(
                    item["brand_name"] ?? (brand ? (brand["brand_name"] ?? brand["name"]) : "") ?? "",
                );
                const rawImage = String(item["image_url"] ?? item["image"] ?? item["image_path"] ?? "");
                const brandId = String(item["brand_id"] ?? (brand ? brand["brand_id"] : "") ?? "").trim();

                /**
                 * Normalize product image URL.
                 * - If raw is filename-only (e.g. "269.jpeg"), it must be resolved as
                 *   /media/products/{brand_id}/{product_id}.{ext} (not relative to /posts).
                 */
                const imageUrl = (() => {
                    const value = rawImage ? String(rawImage).trim().replace(/\\/g, "/") : "";
                    if (!value) return "";

                    if (value.toUpperCase() === "NO_IMAGE") return "";
                    if (isDefaultNoImagePath(value)) return "";

                    // Keep absolute external URLs as-is.
                    if (value.startsWith("http")) return value;

                    // If it already looks like a path, just resolve it.
                    if (value.startsWith("/") || value.includes("/")) {
                        return CommonUtils.resolveUrl(value.startsWith("/") ? value : `/${value}`);
                    }

                    // filename-only: build under /media/products/{brand_id}/{filename}
                    const built = brandId ? phpUrl.productImage(brandId, value) : phpUrl.productImage(value);
                    if (!built || isDefaultNoImagePath(built)) return "";
                    return built.startsWith("http") ? built : CommonUtils.resolveUrl(built);
                })();

                const category = isRecord(item["category"]) ? item["category"] : null;
                const categoryIdRaw = item["category_id"] ?? (category ? category["category_id"] : null);
                const categorySymbol = resolveCategorySymbol(
                    typeof categoryIdRaw === "number" || typeof categoryIdRaw === "string" ? categoryIdRaw : null,
                    (s) => this.form.hasCategoryIconSymbol(s),
                );
                const categoryLineHtml = buildCategoryBreadcrumbHtml({
                    categoryId: categoryIdRaw as number | string | null,
                    categoryName: (item["category_name"] ?? (category ? category["category_name"] : null)) as
                        | string
                        | null,
                    parentCategoryId: (item["parent_category_id"] ??
                        (category ? category["parent_category_id"] : null)) as number | string | null,
                    parentCategoryName: (item["parent_category_name"] ??
                        (category ? category["parent_category_name"] : null)) as string | null,
                });

                const detailUrl = CommonUtils.resolveUrl(`/products/${encodeURIComponent(id)}`);

                const ratingHtml = renderProductRatingStatsHtml({
                    averageRating: item["average_rating"],
                    reviewCount: item["review_count"],
                    includeDataRoles: false,
                });

                // Each of thumb/name gets its OWN small <a> to the product detail page
                // (mirroring card-link.blade.php's separate thumb-link/name-link) rather
                // than one <a> wrapping the whole card — the category breadcrumb below
                // needs its own <a> links (category/parent filters), and HTML forbids
                // nesting an <a> inside another <a> (the browser silently closes the
                // outer one early and reparents everything after it, mangling the DOM).
                return `
                    <div class="search-menu-result" data-product-id="${id}">
                        <div class="search-menu-result__body">
                            <a class="search-menu-result__thumb-link" href="${detailUrl}" target="_blank" rel="noopener noreferrer">
                                <div class="search-menu-result__thumb">
                                    ${buildCategoryPlaceholderHtml(categorySymbol)}
                                    ${imageUrl ? `<img src="${imageUrl}" alt="" loading="lazy" referrerpolicy="no-referrer" onerror="this.onerror=null;this.remove();" />` : ""}
                                </div>
                            </a>
                            <div class="search-menu-result__meta">
                                <a class="search-menu-result__name-link" href="${detailUrl}" target="_blank" rel="noopener noreferrer">
                                    <div class="search-menu-result__name">${name}</div>
                                </a>
                                ${brandName ? `<div class="search-menu-result__brand">${buildBrandIconHtml(24)}${brandName}</div>` : ""}
                                <div class="search-menu-result__category">${buildCategoryIconHtml(categorySymbol, 24)}${categoryLineHtml}</div>
                                <div class="search-menu-result__rating">
                                    ${ratingHtml}
                                </div>
                            </div>
                        </div>
                        <div class="search-menu-result__actions">
                            <button type="button" class="btn-search-set" data-role="${Roles.SEARCH_MENU_SET_PRODUCT}" data-product-id="${id}">
                                <span data-translate="set">ตั้งค่า</span>
                            </button>
                        </div>
                    </div>
                `;
            })
            .filter(Boolean)
            .join("");

        const placeholderRow = brandPlaceholder ? this.buildBrandPlaceholderRowHtml(brandPlaceholder) : "";

        root.innerHTML = `
            <div class="search-menu-results__list">
                ${rows}${placeholderRow}
            </div>
        `;
        translator.updateUITranslations();
    }

    public attachResultsListener() {
        const root = this.menuContainer ? this.form.getSearchMenuResults(this.menuContainer) : null;
        if (!(root instanceof HTMLElement)) return;

        SearchMenuListener.bindClick(root, (e) => {
            const target = e.target instanceof HTMLElement ? e.target : null;
            if (!target) return;

            const setBtn = this.form.getClosestSetProductButton(target);
            if (!setBtn) return;

            const productId = setBtn.getAttribute("data-product-id") || "";
            if (!productId) return;

            const product = this._resultItemsById.get(productId);
            if (!product) return;

            if (this.onSetProduct) {
                this.onSetProduct(product);
            }
        });
    }

    /**
     * Build URLSearchParams for the current selections (does not navigate).
     * @returns {URLSearchParams}
     */
    public buildSearchParams() {
        const params = new URLSearchParams();
        const selections = this.getAllSelections();
        const targetInput = this.menuContainer ? this.form.getSearchMenuTargetInput(this.menuContainer) : null;
        const isPostsTarget = targetInput instanceof HTMLInputElement ? targetInput.checked : false;

        // Scope reads of per-tab fields to the active tab panel
        const activeTabKey = isPostsTarget ? "posts" : "products";
        const activeTabPanel = this.menuContainer
            ? this.form.getAdvancedTabPanel(this.menuContainer, activeTabKey)
            : null;
        const panelScope: ParentNode = activeTabPanel ?? this.menuContainer ?? document;

        // Add review users (multi)
        if (Array.isArray(selections.users) && selections.users.length > 0) {
            const ids = selections.users.map((v) => String(v || "").trim()).filter(Boolean);
            if (ids.length > 0) params.set("user_id", ids.join(","));
        }

        // Add categories (only if not empty).
        if (selections.categories.length > 0) {
            const ids = selections.categories.filter((id) => id && id.trim());
            if (ids.length > 0) {
                const parentIds = [];
                const childIds = [];

                ids.forEach((id) => {
                    const n = Number(id);
                    if (Number.isInteger(n) && n > 0 && n % 100 === 0) {
                        parentIds.push(String(n));
                        return;
                    }
                    childIds.push(String(id));
                });

                if (parentIds.length > 0) {
                    params.set("parent_category_id", parentIds.join(","));
                }
                if (childIds.length > 0) {
                    params.set("category_id", childIds.join(","));
                }
            }
        }

        // Add brands (only if not empty)
        if (selections.brands.length > 0) {
            const ids = selections.brands.filter((id) => id && id.trim());
            if (ids.length > 0) {
                params.set("brand_id", ids.join(","));
            }
        }

        // Add products (only if not empty)
        if (selections.products.length > 0) {
            const value = selections.products.filter((id) => id && id.trim()).join(",");
            if (value) {
                params.set("product_id", value);
            }
        }

        // Add keywords as search query (only if not empty)
        if (selections.keywords.length > 0) {
            const value = selections.keywords.filter((kw) => kw && kw.trim()).join(" ");
            if (value) {
                params.set("search", value);
            }
        }

        // Advanced filters
        try {
            // Preferred source: multi-select hidden binding (advanced_flags)
            const flagsHidden = this.menuContainer
                ? this.form.getNamedHiddenInput(this.menuContainer, "advanced_flags")
                : null;
            const flags =
                flagsHidden instanceof HTMLInputElement ? ChoicesControl.normalizeStringList(flagsHidden.value) : [];

            // Backward compatible: if advanced_flags is not present, fall back to legacy checkboxes
            const newOnlyChecked = (() => {
                if (flags.length > 0) return flags.includes("new_only");
                const el = this.menuContainer ? this.form.getNamedCheckbox(this.menuContainer, "new_only") : null;
                return el instanceof HTMLInputElement ? el.checked : false;
            })();

            const rankingOnlyChecked = (() => {
                if (flags.length > 0) return flags.includes("ranking_only");
                const el = this.menuContainer ? this.form.getNamedCheckbox(this.menuContainer, "ranking_only") : null;
                return el instanceof HTMLInputElement ? el.checked : false;
            })();

            const recommendOnlyChecked = (() => {
                if (flags.length > 0) return flags.includes("recommend_only");
                const el = this.menuContainer ? this.form.getNamedCheckbox(this.menuContainer, "recommend_only") : null;
                return el instanceof HTMLInputElement ? el.checked : false;
            })();

            // reviewed_only and exclude_text_only exist in both tab panels; scope to active panel
            const reviewedOnlyChecked = (() => {
                if (flags.length > 0) return flags.includes("reviewed_only");
                const el = this.form.getNamedCheckbox(panelScope, "reviewed_only");
                return el instanceof HTMLInputElement ? el.checked : false;
            })();

            const excludeTextOnlyChecked = (() => {
                const el = this.form.getNamedCheckbox(panelScope, "exclude_text_only");
                return el instanceof HTMLInputElement ? el.checked : false;
            })();

            if (newOnlyChecked) {
                params.set("new_only", "1");
            }

            if (recommendOnlyChecked) {
                params.set("recommend_only", "1");
            }

            if (rankingOnlyChecked) {
                params.set("ranking_only", "1");
                // Ranking implies sorting by likes_count desc
                params.set("sort", "likes_count");
                params.set("order", "desc");
            }

            if (reviewedOnlyChecked) {
                params.set("reviewed_only", "1");
            }

            if (isPostsTarget && excludeTextOnlyChecked) {
                params.set("exclude_text_only", "1");
            }

            const contentSearch = this.menuContainer
                ? this.form.getNamedInput(this.menuContainer, "content_search")
                : null;
            if (contentSearch instanceof HTMLInputElement) {
                const value = String(contentSearch.value || "").trim();
                if (value) {
                    params.set("content_search", value);

                    // Read content_search_fields from the active tab panel (different options per tab)
                    const fieldsHidden = this.form.getNamedHiddenInput(panelScope, "content_search_fields");
                    if (fieldsHidden instanceof HTMLInputElement) {
                        const fields = ChoicesControl.normalizeStringList(fieldsHidden.value);
                        if (fields.length > 0) {
                            params.set("content_search_fields", fields.join(","));
                        }
                    }
                }
            }

            // rating: multi-select (bound hidden input)
            const ratingHidden = this.menuContainer
                ? this.form.getNamedHiddenInput(this.menuContainer, "rating")
                : null;
            if (ratingHidden instanceof HTMLInputElement) {
                const ratings = ChoicesControl.normalizeStringList(ratingHidden.value);
                if (ratings.length > 0) {
                    params.set("rating", ratings.join(","));
                }
            }

            // price_range: multi-select (bound hidden input)
            const priceRangeHidden = this.menuContainer
                ? this.form.getNamedHiddenInput(this.menuContainer, "price_range")
                : null;
            if (priceRangeHidden instanceof HTMLInputElement) {
                const priceRanges = ChoicesControl.normalizeStringList(priceRangeHidden.value);
                if (priceRanges.length > 0) {
                    params.set("price_range", priceRanges.join(","));
                }
            }

            if (this.isAdminEditor()) {
                const scoredOnlyChecked = (() => {
                    if (flags.length > 0) return flags.includes("scored_only");
                    const el = this.menuContainer
                        ? this.form.getNamedCheckbox(this.menuContainer, "scored_only")
                        : null;
                    return el instanceof HTMLInputElement ? el.checked : false;
                })();
                if (scoredOnlyChecked) params.set("scored_only", "1");

                const updatedBy = this.menuContainer ? this.form.getNamedInput(this.menuContainer, "updated_by") : null;
                if (updatedBy instanceof HTMLInputElement) {
                    const ids = ModalSearchSelectionIO.parseIdsFromValue(updatedBy.value, { mode: "multi" });
                    if (ids.length > 0) params.set("updated_by", ids.join(","));
                }

                // product_status: multi-select writes selected values into hidden input as JSON array string.
                const statusHidden = this.menuContainer
                    ? this.form.getNamedHiddenInput(this.menuContainer, "product_status")
                    : null;
                if (statusHidden instanceof HTMLInputElement) {
                    const values = ChoicesControl.normalizeStringList(statusHidden.value);
                    if (values.length > 0) {
                        params.set("product_status", values.join(","));
                    }
                }

                const from = this.menuContainer ? this.form.getNamedInput(this.menuContainer, "updated_at_from") : null;
                if (from instanceof HTMLInputElement) {
                    const iso = this.toUtcIsoFromDatetimeLocal(from.value);
                    if (iso) params.set("updated_at_from", iso);
                }

                const to = this.menuContainer ? this.form.getNamedInput(this.menuContainer, "updated_at_to") : null;
                if (to instanceof HTMLInputElement) {
                    const iso = this.toUtcIsoFromDatetimeLocal(to.value);
                    if (iso) params.set("updated_at_to", iso);
                }
            }
        } catch (e) {
            console.warn("[SearchMenuRender] Failed to serialize advanced filters", e);
        }

        return params;
    }

    /**
     * Execute search with selected values
     */
    public executeSearch() {
        const targetInput = this.menuContainer ? this.form.getSearchMenuTargetInput(this.menuContainer) : null;
        const target = targetInput?.checked ? "posts" : "products";
        const params = this.buildSearchParams();
        const queryString = params.toString();
        const basePath = target === "posts" ? SearchMenuRender.postsSearchBasePath : "/products";
        const url = queryString ? `${basePath}?${queryString}` : basePath;
        window.location.href = CommonUtils.resolveUrl(url);
    }

    /**
     * Snapshot current selections for persistence.
     * @returns {SearchMenuPersistedState}
     */
    public getPersistedStateSnapshot(): Omit<SearchMenuPersistedState, "version" | "savedAt"> {
        const updatedBy = this.isAdminEditor() ? Array.from(this.selectedUpdatedByIds || []) : [];

        const flagsHidden = this.menuContainer
            ? this.form.getNamedHiddenInput(this.menuContainer, "advanced_flags")
            : null;
        const flags =
            flagsHidden instanceof HTMLInputElement ? ChoicesControl.normalizeStringList(flagsHidden.value) : [];

        // Backward compatible: if advanced_flags is not present, fall back to legacy checkboxes.
        if (flags.length === 0) {
            const newOnlyEl = this.form.getNamedCheckbox(this.menuContainer, "new_only");
            const recommendOnlyEl = this.form.getNamedCheckbox(this.menuContainer, "recommend_only");
            const rankingOnlyEl = this.form.getNamedCheckbox(this.menuContainer, "ranking_only");
            // reviewed_only: read from active panel scope (appears in multiple tab panels)
            const activePanel = this.form.getActiveAdvancedTabPanel(this.menuContainer) ?? this.menuContainer;
            const reviewedOnlyEl = this.form.getNamedCheckbox(activePanel, "reviewed_only");
            if (newOnlyEl instanceof HTMLInputElement && newOnlyEl.checked) flags.push("new_only");
            if (recommendOnlyEl instanceof HTMLInputElement && recommendOnlyEl.checked) flags.push("recommend_only");
            if (rankingOnlyEl instanceof HTMLInputElement && rankingOnlyEl.checked) flags.push("ranking_only");
            if (reviewedOnlyEl instanceof HTMLInputElement && reviewedOnlyEl.checked) flags.push("reviewed_only");
        }

        const fieldsHidden = this.form.getNamedHiddenInput(this.menuContainer, "content_search_fields");
        const fields =
            fieldsHidden instanceof HTMLInputElement ? ChoicesControl.normalizeStringList(fieldsHidden.value) : [];

        const ratingHidden = this.form.getNamedHiddenInput(this.menuContainer, "rating");
        const rating =
            ratingHidden instanceof HTMLInputElement ? ChoicesControl.normalizeStringList(ratingHidden.value) : [];

        const statusHidden = this.form.getNamedHiddenInput(this.menuContainer, "product_status");
        const status =
            statusHidden instanceof HTMLInputElement ? ChoicesControl.normalizeStringList(statusHidden.value) : [];

        const contentSearch = this.form.getNamedInput(this.menuContainer, "content_search");
        const contentSearchValue = contentSearch instanceof HTMLInputElement ? String(contentSearch.value || "") : "";

        const from = this.form.getNamedInput(this.menuContainer, "updated_at_from");
        const to = this.form.getNamedInput(this.menuContainer, "updated_at_to");

        return {
            selections: {
                categories: Array.from(this.selectedCategoryIds || []),
                brands: Array.from(this.selectedBrandIds || []),
                users: Array.from(this.selectedUserIds || []),
                updated_by: updatedBy,
            },
            advanced: {
                advanced_flags: flags,
                content_search: contentSearchValue,
                content_search_fields: fields,
                rating,
                product_status: status,
                updated_at_from: from instanceof HTMLInputElement ? String(from.value || "") : "",
                updated_at_to: to instanceof HTMLInputElement ? String(to.value || "") : "",
            },
        };
    }

    /**
     * Apply a persisted snapshot to the current UI (menu must be rendered).
     * @param {SearchMenuPersistedState | null} raw
     */
    public applyPersistedState(raw: SearchMenuPersistedState | null): void {
        if (!this.menuContainer) return;
        const state = raw;
        if (!state) return;

        const selections = state.selections ?? null;
        const advanced = state.advanced ?? null;

        const categories = selections ? ChoicesControl.normalizeStringList(selections["categories"]) : [];
        const brands = selections ? ChoicesControl.normalizeStringList(selections["brands"]) : [];
        const users = selections ? ChoicesControl.normalizeStringList(selections["users"]) : [];
        const updatedBy = selections ? ChoicesControl.normalizeStringList(selections["updated_by"]) : [];

        this.selectedCategoryIds = new Set(categories);
        this._categoryPicker?.setSelectedIds?.(categories);
        this._categoryPicker?.render?.();

        this.selectedBrandIds = new Set(brands);
        this._brandPicker?.setSelectedIds?.(brands);
        this._brandPicker?.render?.();

        this.selectedUserIds = new Set(users);
        this._userPicker?.setSelectedIds?.(users);
        this._userPicker?.render?.();

        if (this.isAdminEditor()) {
            this.selectedUpdatedByIds = new Set(updatedBy);
            const hiddenUpdatedBy = this.form.getNamedHiddenInput(this.menuContainer, "updated_by");
            if (hiddenUpdatedBy instanceof HTMLInputElement) {
                hiddenUpdatedBy.value = ModalSearchSelectionIO.serializeIds(updatedBy, { mode: "multi" });
            }
            this._productUpdatedByPicker?.setSelectedIds?.(updatedBy);
            this._productUpdatedByPicker?.render?.();
        }

        if (advanced) {
            const setBound = (
                name: string,
                values: SearchMenuPersistedAdvanced[keyof SearchMenuPersistedAdvanced],
            ): void => {
                const hidden = this.form.getNamedHiddenInput(this.menuContainer, name);
                if (!(hidden instanceof HTMLInputElement)) return;

                const list = ChoicesControl.normalizeStringList(values);
                hidden.value = JSON.stringify(list);

                const select = this.form.getChoicesSelect(this.menuContainer, name);
                if (!(select instanceof HTMLSelectElement)) return;

                const instUnknown = this.getChoicesInstance(select);

                ChoicesControl.clearSelection(select, instUnknown);
                if (list.length > 0) {
                    list.forEach((v) => this.setChoiceByValue(instUnknown, v));
                    select.dispatchEvent(new Event("change", { bubbles: true }));
                }
            };

            const contentSearch = this.form.getNamedInput(this.menuContainer, "content_search");
            if (contentSearch instanceof HTMLInputElement) {
                contentSearch.value = String(advanced["content_search"] ?? "");
            }

            // Reflect advanced_flags into toggle/checkbox fields when the multi-select is not present.
            const flags = ChoicesControl.normalizeStringList(advanced["advanced_flags"]);
            const newOnlyEl = this.form.getNamedCheckbox(this.menuContainer, "new_only");
            if (newOnlyEl instanceof HTMLInputElement) newOnlyEl.checked = flags.includes("new_only");
            const recommendOnlyEl = this.form.getNamedCheckbox(this.menuContainer, "recommend_only");
            if (recommendOnlyEl instanceof HTMLInputElement) recommendOnlyEl.checked = flags.includes("recommend_only");
            const rankingOnlyEl = this.form.getNamedCheckbox(this.menuContainer, "ranking_only");
            if (rankingOnlyEl instanceof HTMLInputElement) rankingOnlyEl.checked = flags.includes("ranking_only");
            // reviewed_only appears in multiple tab panels — apply to all
            const applyPanels = this.form.getAllAdvancedTabPanels(this.menuContainer);
            (applyPanels.length > 0 ? applyPanels : [this.menuContainer]).forEach((panel) => {
                const el = this.form.getNamedCheckbox(panel, "reviewed_only");
                if (el instanceof HTMLInputElement) el.checked = flags.includes("reviewed_only");
            });

            setBound("advanced_flags", advanced["advanced_flags"]);
            setBound("content_search_fields", advanced["content_search_fields"]);
            setBound("rating", advanced["rating"]);

            if (this.isAdminEditor()) {
                setBound("product_status", advanced["product_status"] ?? advanced["status"]);

                const from = this.form.getNamedInput(this.menuContainer, "updated_at_from");
                if (from instanceof HTMLInputElement) {
                    this.syncKeywordClearButton();
                    from.value = String(advanced["updated_at_from"] ?? "");
                }
                const to = this.form.getNamedInput(this.menuContainer, "updated_at_to");
                if (to instanceof HTMLInputElement) {
                    to.value = String(advanced["updated_at_to"] ?? "");
                }
            }
        }

        this.syncAdvancedPanelExpandedState();
        this.syncTargetFieldVisibility();
    }

    /**
     * Get all selections from Choices instances
     * @returns {Object}
     */
    public getAllSelections(): SearchMenuSelections {
        return {
            categories: Array.from(this.selectedCategoryIds),
            brands: Array.from(this.selectedBrandIds),
            users: Array.from(this.selectedUserIds),
            // Popular Products / Popular Keywords selects were removed.
            // Keep these keys to avoid breaking callers.
            products: [],
            keywords: [],
        };
    }

    public getChoicesInstance(select: HTMLSelectElement | null | undefined): ChoicesInstance | null {
        if (!(select instanceof HTMLSelectElement)) {
            return null;
        }

        return ChoicesControlUtils.asInternalSelect(select).__choicesInstance ?? null;
    }

    public setChoiceByValue(instance: ChoicesInstance | null, value: string): void {
        if (typeof instance?.setChoiceByValue !== "function") {
            return;
        }

        instance.setChoiceByValue(String(value));
    }

    public clearContentSearchFields(): void {
        if (!this.menuContainer) return;

        const fieldsHidden = this.form.getNamedHiddenInput(this.menuContainer, "content_search_fields");
        const select = this.form.getChoicesSelect(this.menuContainer, "content_search_fields");

        if (fieldsHidden instanceof HTMLInputElement) {
            fieldsHidden.value = JSON.stringify([]);
        }

        if (!(select instanceof HTMLSelectElement)) {
            return;
        }

        Array.from(select.options).forEach((option) => {
            option.selected = false;
        });

        const inst = this.getChoicesInstance(select);
        ChoicesControl.clearSelection(select, inst);
        select.dispatchEvent(new Event("change", { bubbles: true }));
    }

    /**
     * Clear all selections
     */
    public clearAllSelections() {
        this.selectedCategoryIds.clear();
        this._categoryPicker?.setSelectedIds?.([]);
        this._categoryPicker?.render?.();

        this.selectedBrandIds.clear();
        this._brandPicker?.setSelectedIds?.([]);
        this._brandPicker?.render?.();

        this.selectedUserIds.clear();
        this._userPicker?.setSelectedIds?.([]);
        this._userPicker?.render?.();

        if (this.isAdminEditor()) {
            this.selectedUpdatedByIds.clear();
            this._productUpdatedByPicker?.setSelectedIds?.([]);
            this._productUpdatedByPicker?.render?.();
        }

        // Clear advanced filters (so the Clear button truly resets the search).
        try {
            const uncheck = (name) => {
                const el = this.menuContainer ? this.form.getNamedCheckbox(this.menuContainer, name) : null;
                if (el instanceof HTMLInputElement) el.checked = false;
            };

            const clearValue = (name) => {
                const el = this.menuContainer ? this.form.getNamedInput(this.menuContainer, name) : null;
                if (el instanceof HTMLInputElement) el.value = "";
            };

            uncheck("new_only");
            uncheck("recommend_only");
            uncheck("ranking_only");
            // reviewed_only and exclude_text_only appear in multiple tab panels — clear all
            const allPanels = this.menuContainer ? this.form.getAllAdvancedTabPanels(this.menuContainer) : [];
            const uncheckInAll = (name: string) => {
                if (allPanels.length > 0) {
                    allPanels.forEach((panel) => {
                        const el = this.form.getNamedCheckbox(panel, name);
                        if (el instanceof HTMLInputElement) el.checked = false;
                    });
                } else {
                    uncheck(name);
                }
            };
            uncheckInAll("reviewed_only");
            uncheckInAll("exclude_text_only");
            clearValue("content_search");
            this.syncKeywordClearButton();

            // Clear rating multi-select
            const ratingSelect = this.menuContainer ? this.form.getChoicesSelect(this.menuContainer, "rating") : null;
            if (ratingSelect instanceof HTMLSelectElement) {
                const inst = this.getChoicesInstance(ratingSelect);
                ChoicesControl.clearSelection(ratingSelect, inst);
            }

            // Clear advanced_flags choices multi-select
            const flagsSelect = this.menuContainer
                ? this.form.getChoicesSelect(this.menuContainer, "advanced_flags")
                : null;
            if (flagsSelect instanceof HTMLSelectElement) {
                const inst = this.getChoicesInstance(flagsSelect);
                ChoicesControl.clearSelection(flagsSelect, inst);
            }

            this.clearContentSearchFields();

            if (this.isAdminEditor()) {
                clearValue("updated_by");
                clearValue("updated_at_from");
                clearValue("updated_at_to");

                // Clear status choices multi-select
                const statusSelect = this.menuContainer
                    ? this.form.getChoicesSelect(this.menuContainer, "product_status")
                    : null;
                if (statusSelect instanceof HTMLSelectElement) {
                    const inst = this.getChoicesInstance(statusSelect);
                    ChoicesControl.clearSelection(statusSelect, inst);
                }
            }
        } catch (e) {
            console.warn("[SearchMenuRender] Failed to clear advanced filters", e);
        }
    }

    /**
     * When the search menu is reopened, it is not re-rendered once initialized.
     * That means any previous in-modal selection can persist and look like a "default".
     *
     * Source of truth for "active filters" is the current URL query params.
     * If the URL does not activate content_search/content_search_fields, clear stale in-memory selections.
     */
    public applyContentSearchDefaultsIfInactive() {
        try {
            if (!this.menuContainer) return;

            const params = new URLSearchParams(globalThis?.location?.search || "");
            const urlKeyword = String(params.get("content_search") || "").trim();

            // If the keyword is not active in the URL, content_search_fields is not meaningful.
            if (urlKeyword !== "") return;

            // Reset keyword input to URL (empty).
            const contentSearch = this.form.getNamedInput(this.menuContainer, "content_search");
            if (contentSearch instanceof HTMLInputElement) {
                contentSearch.value = "";
            }

            this.syncKeywordClearButton();

            this.clearContentSearchFields();
        } catch (e) {
            console.warn("[SearchMenuRender] Failed to clear content_search_fields on reopen", e);
        }
    }

    /**
     * Attach close button listener.
     */
    public attachCloseListener(onClose: (() => void) | null): void {
        if (!this.menuContainer) return;

        const closeBtn = this.form.getSearchMenuCloseButton(this.menuContainer);
        if (closeBtn) {
            SearchMenuListener.bindClick(closeBtn, (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onClose) onClose();
            });
        }
    }

    /**
     * Destroy the menu and cleanup Choices instances
     */
    public destroy() {
        if (this.menuContainer) {
            ChoicesControl.destroyAll(this.menuContainer);
        }

        if (this.menuContainer) {
            this.menuContainer.innerHTML = "";
            this.menuContainer = null;
        }
    }
}

function isRecord(value: unknown): value is SearchMenuRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}
